import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { getCoachingOrgId } from "../services/coaching-org-context";
import { getCoachingZ6Services } from "../services/coaching-service-provider";
import type { CoachingOperationResult } from "../services/coaching-dtos";
import type {
  CoachingUseCaseCreateRequest,
  PostSessionEmailRequest
} from "../services/coaching-z6-dtos";
import { coachingQueryKeys, isAggregateSessionListContaining } from "./coaching-query-keys";
import { cacheScopedParticipantRemoval } from "./coaching-write-truth-caches";
import { createIdempotencyKey } from "./useCoachingQueries";

/**
 * Z6 action lanes (F4 resend invites, F9 post-session email, F13 cohort
 * reassign, F14 use-case create). Each mutation mints a UUID Idempotency-Key
 * and invalidates the read surface it feeds (F4 -> invite log, F9 -> email log,
 * F14 -> use-case list). Backend gate state (dry_run/queued) is returned to the
 * caller untouched so the UI can label it honestly.
 */
export function useCoachingZ6Mutations() {
  const queryClient = useQueryClient();
  const services = getCoachingZ6Services();

  return {
    // F4: resend Zoom invites to all (undefined ids) or selected participants.
    resendInvites: useMutation({
      mutationFn: ({ sessionId, participantIds }: { sessionId: string; participantIds?: number[] }) =>
        services.invites.resend(sessionId, participantIds, createIdempotencyKey(`z6-resend-invites:${sessionId}`)),
      onSuccess: (_result, variables) =>
        queryClient.invalidateQueries({ queryKey: coachingQueryKeys.z6.inviteLog(variables.sessionId) })
    }),
    // F9: trigger the standardized post-session email to all participants.
    postSessionEmail: useMutation({
      mutationFn: ({ sessionId, body }: { sessionId: string; body: PostSessionEmailRequest }) =>
        services.postSessionEmail.enqueue(sessionId, body, createIdempotencyKey(`z6-post-session-email:${sessionId}`)),
      onSuccess: (_result, variables) =>
        queryClient.invalidateQueries({ queryKey: coachingQueryKeys.z6.postSessionEmailLog(variables.sessionId) })
    }),
    // F13: reassign an existing cohort to a new coach (admin lane). The
    // response contains reassignment metadata, not durable cohort/session rows,
    // so invalidate only the displayed coach-bearing projections.
    reassignCohortCoach: useMutation({
      mutationFn: ({ cohortId, newCoachId, notes }: { cohortId: string; newCoachId: number; notes?: string }) =>
        services.cohortReassign.reassignCoach(cohortId, newCoachId, notes, createIdempotencyKey(`z6-cohort-reassign:${cohortId}`)),
      onSuccess: (_result, variables) => invalidateCohortCoachReadbacks(queryClient, variables.cohortId)
    }),
    // F14: submit a coach use case to the central repository.
    createUseCase: useMutation({
      mutationFn: (body: CoachingUseCaseCreateRequest) =>
        services.useCases.create(body, createIdempotencyKey("z6-use-case-create")),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: coachingQueryKeys.z6.useCases() })
    }),
    // F6: remove a participant from a cohort (admin lane). The response carries
    // the inactive participant row, so drop it from active list caches first;
    // the affected session/history/scope projections still need a refetch.
    removeParticipant: useMutation({
      mutationFn: ({ cohortId, participantId }: { cohortId: string; participantId: string }) =>
        services.participants.removeFromCohort(cohortId, participantId, createIdempotencyKey(`z6-remove-participant:${cohortId}:${participantId}`)),
      onSuccess: async (result, variables) => {
        const operation = result as CoachingOperationResult;
        if (operation.participant && typeof operation.participant === "object") {
          await cacheScopedParticipantRemoval(queryClient, operation, {
            cohortId: variables.cohortId,
            participantId: variables.participantId,
            orgId: getCoachingOrgId()
          });
          await invalidateParticipantRemovalReadbacks(queryClient, variables.cohortId, false);
          return;
        }
        await invalidateParticipantListReadbacks(queryClient, variables.cohortId, variables.participantId);
        await invalidateParticipantRemovalReadbacks(queryClient, variables.cohortId);
      }
    })
  };
}

async function invalidateCohortCoachReadbacks(queryClient: QueryClient, cohortId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.cohortDetail(cohortId) }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("cohorts.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("participants.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("participants.listForCohort", { cohort_id: cohortId }) }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("sessions.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohortId }) }),
    queryClient.invalidateQueries({ predicate: (query) => isAggregateSessionListContaining(query.queryKey, cohortId) }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("history.list") }),
    queryClient.invalidateQueries({ queryKey: [...coachingQueryKeys.root, "dashboard"] }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.recordScopeRoot() })
  ]);
}

async function invalidateParticipantListReadbacks(queryClient: QueryClient, cohortId: string, participantId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("participants.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("participants.listForCohort", { cohort_id: cohortId }) }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.participantDetail(participantId) })
  ]);
}

async function invalidateParticipantRemovalReadbacks(queryClient: QueryClient, cohortId: string, includeShared = true) {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("sessions.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohortId }) }),
    queryClient.invalidateQueries({ predicate: (query) => isAggregateSessionListContaining(query.queryKey, cohortId) })
  ];
  if (includeShared) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("history.list") }),
      queryClient.invalidateQueries({ queryKey: coachingQueryKeys.recordScopeRoot() }),
      queryClient.invalidateQueries({ queryKey: [...coachingQueryKeys.root, "dashboard"] })
    );
  }
  await Promise.all(invalidations);
}
